import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Font_Size = () => {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🔠 CSS Font Size</h1>

      {/* Intro Gradient Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #d1fae5, #bbf7d0)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#166534", marginBottom: "0.5rem" }}>
          ✨ What is Font Size?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          The <code>font-size</code> property in CSS sets the size of the text.
          It plays a big role in readability, accessibility, and responsive
          design. You can use absolute units, relative units, or percentages
          depending on your needs.
        </p>
      </div>

      {/* Explanation */}
      <div className="tutorial-section">
        <h2 className="section-title">📖 Ways to Define Font Size</h2>
        <ul className="tutorial-list">
          <li>
            <strong>Absolute size</strong> – Keywords like <code>small</code>,{" "}
            <code>medium</code>, <code>large</code>.
          </li>
          <li>
            <strong>Length units</strong> – <code>px</code> (pixels),{" "}
            <code>pt</code> (points), etc.
          </li>
          <li>
            <strong>Relative units</strong> – <code>em</code> (relative to
            parent), <code>rem</code> (relative to root{" "}
            <code>&lt;html&gt;</code> size).
          </li>
          <li>
            <strong>Percentages</strong> – Relative to the parent element’s font
            size.
          </li>
          <li>
            <strong>Viewport units</strong> – <code>vw</code> (relative to
            viewport width), <code>vh</code> (relative to viewport height).
          </li>
        </ul>
      </div>

      {/* Code Example */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Examples</h2>

        <CodeCopyBlock
          code={`p.small { font-size: 12px; }   /* absolute size in pixels */
p.medium { font-size: 1em; }    /* relative to parent */
p.large { font-size: 150%; }   /* percentage */
p.responsive { font-size: 5vw; } /* responsive to viewport width */`}
        />

        {/* Live Preview */}
        <p style={{ fontSize: "12px" }}>This is 12px text (small).</p>
        <p style={{ fontSize: "1em" }}>This is 1em text (medium, relative).</p>
        <p style={{ fontSize: "150%" }}>
          This is 150% text (relative to parent).
        </p>
        <p style={{ fontSize: "5vw" }}>
          This is responsive text (5vw – scales with screen width).
        </p>
      </div>

      {/* Notes */}
      <div className="tutorial-section">
        <h2 className="section-title">💡 Notes & Best Practices</h2>
        <ul className="tutorial-list">
          <li>
            Using <code>px</code> gives fixed sizes, but may not scale well on
            all devices.
          </li>
          <li>
            Use <code>em</code> and <code>rem</code> for better accessibility
            and responsiveness.
          </li>
          <li>
            <code>rem</code> is recommended for consistency, since it’s based on
            the root <code>html</code> font size.
          </li>
          <li>
            Use <code>vw</code>/<code>vh</code> for truly fluid, responsive
            typography.
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          After mastering <code>font-size</code>, continue with{" "}
          <a href="/css/font-family" className="learn-more">
            CSS Font Family
          </a>{" "}
          to explore how to choose and apply fonts effectively.
        </p>
      </div>
    </div>
  );
};

export default Font_Size;
